/**
 * Utilidad para desofuscar llaves de encriptación
 * 
 * IMPORTANTE: Esta ofuscación NO es 100% segura. Es solo para dificultar
 * el acceso casual a la llave. Cualquier persona con conocimientos técnicos
 * puede extraer la llave del código compilado.
 * 
 * Para máxima seguridad, mover la lógica de encriptación al backend.
 */

/**
 * Desofusca una llave que fue ofuscada usando el proceso inverso
 * 
 * Proceso de desofuscación:
 * 1. Decodificar de Base64
 * 2. Dividir la cadena en fragmentos
 * 3. Remover caracteres de relleno (cada 3er carácter)
 * 4. Revertir el orden de los fragmentos
 * 5. Unir para obtener la llave original
 * 
 * @param obfuscatedKey - Llave ofuscada en formato Base64
 * @returns Llave original desofuscada
 */
export function deobfuscateKey(obfuscatedKey: string): string {
  if (!obfuscatedKey || obfuscatedKey.trim() === '') {
    throw new Error('_s3c no puede estar vacía');
  }

  try {
    // Paso 1: Decodificar de Base64
    let decoded = atob(obfuscatedKey);

    // Paso 2: Remover caracteres de relleno (cada 3er carácter)
    let cleaned = '';
    for (let i = 0; i < decoded.length; i++) {
      // Solo mantener caracteres que NO están en posiciones múltiplos de 3
      if ((i + 1) % 3 !== 0) {
        cleaned += decoded[i];
      }
    }

    // Paso 3: Dividir en fragmentos de 4 caracteres
    let fragments: string[] = [];
    for (let i = 0; i < cleaned.length; i += 4) {
      fragments.push(cleaned.substring(i, i + 4));
    }

    // Paso 4: Revertir el orden de los fragmentos
    let reversed = fragments.reverse();

    // Paso 5: Unir para obtener la llave original
    return reversed.join('');

  } catch (error) {
    throw new Error(`Error al _s3c: ${error instanceof Error ? error.message : 'Error desconocido'}`);
  }
}

/**
 * Ofusca una llave para almacenamiento en archivos de environment
 * 
 * NOTA: Esta función está aquí solo para referencia y desarrollo.
 * No debe ser usada en producción. Usa el script generate-obfuscated-key.js
 * 
 * Proceso de ofuscación:
 * 1. Dividir la llave en fragmentos de 4 caracteres
 * 2. Revertir el orden de los fragmentos
 * 3. Insertar caracteres de relleno aleatorios (cada 3 caracteres)
 * 4. Codificar en Base64
 * 
 * @param plainKey - Llave en texto plano
 * @returns Llave ofuscada en formato Base64
 */
export function obfuscateKey(plainKey: string): string {
  if (!plainKey || plainKey.trim() === '') {
    throw new Error('_s3c no puede estar vacía');
  }

  try {
    // Paso 1: Dividir en fragmentos de 4 caracteres
    let fragments: string[] = [];
    for (let i = 0; i < plainKey.length; i += 4) {
      fragments.push(plainKey.substring(i, i + 4));
    }

    // Paso 2: Revertir el orden
    let reversed = fragments.reverse().join('');

    // Paso 3: Insertar caracteres de relleno aleatorios cada 2 caracteres
    let obfuscated = '';
    let paddingChars = 'xyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

    for (let i = 0; i < reversed.length; i++) {
      obfuscated += reversed[i];

      // Cada 2 caracteres, insertar un carácter de relleno
      if ((i + 1) % 2 === 0 && i < reversed.length - 1) {
        let randomChar = paddingChars[Math.floor(Math.random() * paddingChars.length)];
        obfuscated += randomChar;
      }
    }

    // Paso 4: Codificar en Base64
    return btoa(obfuscated);

  } catch (error) {
    throw new Error(`Error al _s3c: ${error instanceof Error ? error.message : 'Error desconocido'}`);
  }
}
